import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/")({
  component: HomePage,
});

function FeatureCard({
  icon,
  title,
  description,
  delay,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: string;
}) {
  return (
    <div
      className="group relative bg-gradient-to-br from-stone-800/80 to-stone-900/90 backdrop-blur-sm rounded-2xl p-6 border border-amber-900/20 hover:border-amber-600/40 transition-all duration-500 hover:shadow-xl hover:shadow-amber-900/10"
      style={{ animationDelay: delay }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-amber-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
      <div className="relative">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center mb-4 text-amber-400 group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <h3 className="text-lg font-semibold text-stone-100 mb-2">{title}</h3>
        <p className="text-stone-400 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function StepIndicator({
  step,
  title,
  description,
  isLast,
}: {
  step: number;
  title: string;
  description: string;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-stone-900 font-bold text-sm shrink-0">
          {step}
        </div>
        {!isLast && (
          <div className="w-px h-full bg-gradient-to-b from-amber-500/50 to-transparent min-h-[3rem]" />
        )}
      </div>
      <div className="pb-8">
        <h4 className="text-stone-100 font-medium mb-1">{title}</h4>
        <p className="text-stone-400 text-sm">{description}</p>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950 text-stone-100">
      {/* Subtle texture overlay */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+CjxyZWN0IHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgZmlsbD0idHJhbnNwYXJlbnQiPjwvcmVjdD4KPGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSI+PC9jaXJjbGU+Cjwvc3ZnPg==')] opacity-50 pointer-events-none" />

      {/* Hero Section */}
      <section className="relative py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              <img
                src="/web-app-manifest-512x512.png"
                alt="Goose"
                width={72}
                height={72}
                className="drop-shadow-lg"
              />
              <h1 className="text-6xl font-bold tracking-tight">Goose</h1>
            </div>
          </div>

          <p className="text-4xl sm:text-5xl font-bold mb-6 tracking-tight">
            <span className="bg-gradient-to-r from-stone-100 via-amber-100 to-stone-200 bg-clip-text text-transparent">
              The Account Manager&rsquo;s
            </span>
            <br />
            <span className="bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent">
              AI Wingman
            </span>
          </p>

          <p className="text-lg sm:text-xl text-stone-400 max-w-2xl mb-6 mx-auto leading-relaxed">
            Assess the Talent&apos;s fit for a role before engaging the Account
            or Hiring Manager. Go from Opportunity to evaluation in seconds, not
            hours.
          </p>

          <div className="inline-flex align-baseline items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-12">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            A Lab Experiment
          </div>

          {/* Demo Video */}
          <div className="max-w-3xl mx-auto">
            <p className="text-stone-500 text-sm mb-4 flex items-center justify-center gap-2">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z"
                />
              </svg>
              See it in action
            </p>
            <div className="rounded-xl overflow-hidden bg-stone-900/50 border border-stone-800">
              <div
                className="relative w-full"
                style={{ paddingBottom: "56.25%" }}
              >
                <iframe
                  src="https://allegiscloud-my.sharepoint.com/personal/trolson_teksystems_com/_layouts/15/embed.aspx?UniqueId=9bb50426-4ee9-44fa-8a25-31c08d88d4ae&embed=%7B%22hvm%22%3Atrue%2C%22ust%22%3Atrue%7D&referrer=StreamWebApp&referrerScenario=EmbedDialog.Create"
                  width="640"
                  height="360"
                  allowFullScreen
                  title="Goose Demo"
                  className="absolute inset-0 w-full h-full border-0 overflow-hidden"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              What makes Goose special?
            </h2>
            <p className="text-stone-400 max-w-xl mx-auto">
              Powered by advanced LLMs to understand context, not just match
              keywords
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              delay="0ms"
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
                  />
                </svg>
              }
              title="Instant Requirement Parsing"
              description="Automatically extract structured requirements from any job description. Skills, experience, and location are all parsed in seconds."
            />

            <FeatureCard
              delay="50ms"
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                  />
                </svg>
              }
              title="Contextual Fit Scoring"
              description="Not just keyword matching. Understand the context behind requirements to determine true candidate fit."
            />

            <FeatureCard
              delay="100ms"
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                  />
                </svg>
              }
              title="Conversational Refinement"
              description="Refine requirements or add candidate info through natural conversation. No forms, no friction."
            />

            <FeatureCard
              delay="150ms"
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              }
              title="Transparent Reasoning"
              description="See exactly where candidates excel and where there are gaps. No black boxes. Understand the 'why' behind every score."
            />

            <FeatureCard
              delay="200ms"
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 002.25-2.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v2.25A2.25 2.25 0 006 10.5zm0 9.75h2.25A2.25 2.25 0 0010.5 18v-2.25a2.25 2.25 0 00-2.25-2.25H6a2.25 2.25 0 00-2.25 2.25V18A2.25 2.25 0 006 20.25zm9.75-9.75H18a2.25 2.25 0 002.25-2.25V6A2.25 2.25 0 0018 3.75h-2.25A2.25 2.25 0 0013.5 6v2.25a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
              }
              title="Connected Integration"
              description="Pull opportunities directly from Salesforce and update profiles without leaving the workflow."
            />

            <FeatureCard
              delay="250ms"
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
                  />
                </svg>
              }
              title="Real-time Updates"
              description="Requirements update as you refine them. Fit scores recalculate as new information is added. Everything stays in sync."
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: Steps */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-8">
                How it works
              </h2>
              <div className="space-y-2">
                <StepIndicator
                  step={1}
                  title="Import the Opportunity"
                  description="Goose automatically imports and parses requirements from Connected or paste in any job description."
                />
                <StepIndicator
                  step={2}
                  title="Refine with Conversation"
                  description="Work with Goose to clarify ambiguous requirements. 'Python is the only required language.' Done."
                />
                <StepIndicator
                  step={3}
                  title="Evaluate Candidates"
                  description="Pull in candidate profiles and get instant contextual fit analysis with detailed gap identification."
                />
                <StepIndicator
                  step={4}
                  title="Add Informal Insights"
                  description="Mention new certifications or experience from emails? Goose incorporates it and recalculates fit."
                  isLast
                />
              </div>
            </div>

            {/* Right: Quote Card */}
            <div className="lg:sticky lg:top-8">
              <div className="bg-gradient-to-br from-stone-800/60 to-stone-900/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-900/20">
                <svg
                  className="w-10 h-10 text-amber-500/40 mb-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
                </svg>
                <blockquote className="text-stone-200 text-lg leading-relaxed mb-6">
                  Goose doesn&rsquo;t just give you a score; it shows you why.
                  Recruiters can see exactly where candidates excel and where
                  there might be gaps.
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-stone-900 font-bold text-sm">
                    G
                  </div>
                  <div>
                    <div className="text-stone-100 font-medium">
                      Goose Mission
                    </div>
                    <div className="text-stone-500 text-sm">
                      Understand fit, not just keywords
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-stone-800/40 rounded-xl p-4 border border-stone-700/50 text-center">
                  <div className="text-2xl font-bold text-amber-400">~30s</div>
                  <div className="text-stone-500 text-sm">
                    To parse requirements
                  </div>
                </div>
                <div className="bg-stone-800/40 rounded-xl p-4 border border-stone-700/50 text-center">
                  <div className="text-2xl font-bold text-amber-400">100%</div>
                  <div className="text-stone-500 text-sm">
                    Transparent reasoning
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experiment Goals Section */}
      <section className="relative py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-amber-500/5 to-amber-600/5 rounded-2xl p-8 border border-amber-500/10">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-amber-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
                  />
                </svg>
              </span>
              What we&rsquo;re learning
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                <p className="text-stone-300">
                  How often our data tells the right story about why Talent is a
                  good fit, versus that insight living informally in
                  Recruiters&rsquo; and Account Managers&rsquo; heads.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                <p className="text-stone-300">
                  LLM-based fit evaluation between Talent and role requirements,
                  moving beyond keyword matching.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                <p className="text-stone-300">
                  Conversational AI to capture informal insights and
                  automatically update Connected, without adding extra work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative pt-16 pb-8 px-6 border-t border-stone-800/50">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-stone-500">
          <div className="flex items-center gap-2">
            <img
              src="/web-app-manifest-512x512.png"
              alt="Goose"
              width={20}
              height={20}
              className="opacity-50"
            />
            <span>Goose: A Lab Experiment</span>
          </div>
          <div className="text-stone-600">
            The Account Manager&rsquo;s AI Wingman
          </div>
        </div>
      </footer>
    </div>
  );
}
